import { AxiosError } from 'axios';
import { put, call, takeLatest, select } from 'redux-saga/effects';
import { API } from '../api/base';
import get from 'lodash.get';
import {
  types,
  Customer,
  SaveCustomerAsyncRequest,
  SaveSurveyAsyncRequest,
  GetCustomerAsyncRequest,
  GetLocationsAsyncRequest,
  Location
} from './types';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as cartSelectors, Cart } from 'services/cart';
import { getErrorCode, getErrorMessage } from 'services/api/normalizer';
import i18n from 'services/translation/i18n';

export function* getCustomerAsync({ payload }: GetCustomerAsyncRequest) {
  yield put({ type: types.GET_CUSTOMER_ASYNC.PENDING, payload: true });

  try {
    yield put({ type: types.SET_LOOKUP_DATA, payload });
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { customerParams }: Cart = yield select(cartSelectors.selectCart);

    const apiPayload = {
      ...payload,
      customerParams
    };
    const customerAPI = new API(`ticketing/customer/search?session=${session}`);
    const response: Customer = yield call(customerAPI.create, apiPayload);

    yield put({ type: types.GET_CUSTOMER_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
    const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
    if (code === 400) {
      yield put({
        type: types.GET_CUSTOMER_ASYNC.ERROR,
        payload: { message: i18n.t('validation.invalidEmail'), code }
      });
    } else if ((code as number) >= 500) {
      yield put({
        type: types.GET_CUSTOMER_ASYNC.ERROR,
        payload: { message: i18n.t('sagaError.internetDown', { support }), code }
      });
    } else {
      yield put({
        type: types.GET_CUSTOMER_ASYNC.ERROR,
        payload: { message: i18n.t('sagaError.somethingWrong', { support }), code }
      });
    }
  } finally {
    yield put({ type: types.GET_CUSTOMER_ASYNC.PENDING, payload: false });
  }
}

export function* saveCustomerAsync({ payload }: SaveCustomerAsyncRequest) {
  yield put({ type: types.SAVE_CUSTOMER_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { customerParams }: Cart = yield select(cartSelectors.selectCart);

    const customerPayload = {
      ...payload,
      customerParams
    };
    const customerAPI = new API(`ticketing/customer?session=${session}`);
    const response: Customer = yield call(customerAPI.create, customerPayload);

    yield put({ type: types.SAVE_CUSTOMER_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
    const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
    if (code === 400) {
      yield put({ type: types.SAVE_CUSTOMER_ASYNC.ERROR, payload: i18n.t('validation.invalidEmail') });
    } else if ((code as number) >= 500) {
      yield put({ type: types.SAVE_CUSTOMER_ASYNC.ERROR, payload: i18n.t('sagaError.internetDown', { support }) });
    } else {
      yield put({ type: types.SAVE_CUSTOMER_ASYNC.ERROR, payload: i18n.t('sagaError.somethingWrong', { support }) });
    }
  } finally {
    yield put({ type: types.SAVE_CUSTOMER_ASYNC.PENDING, payload: false });
  }
}

export function* saveSurveyAsync({ payload }: SaveSurveyAsyncRequest) {
  yield put({ type: types.SAVE_SURVEY_ASYNC.PENDING, payload: true });
  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const surveyAPI = new API(`ticketing/saveSurvey?session=${session}`);
    yield call(surveyAPI.create, { ...payload });

    yield put({ type: types.SAVE_SURVEY_ASYNC.SUCCESS, payload: payload.details });
  } catch (err) {
    const code = getErrorCode(err as AxiosError);
    const config: Configuration = yield select(configSelectors.selectConfiguration);
    const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
    const support = get(config, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
    if (code === 400) {
      yield put({ type: types.SAVE_SURVEY_ASYNC.ERROR, payload: i18n.t('sagaError.checkInput') });
    } else if ((code as number) >= 500) {
      yield put({ type: types.SAVE_SURVEY_ASYNC.ERROR, payload: i18n.t('sagaError.internetDown', { support }) });
    } else {
      yield put({ type: types.SAVE_SURVEY_ASYNC.ERROR, payload: i18n.t('sagaError.somethingWrong', { support }) });
    }
  } finally {
    yield put({ type: types.SAVE_SURVEY_ASYNC.PENDING, payload: false });
  }
}

export function* getLocationsAsync({ payload }: GetLocationsAsyncRequest) {
  yield put({ type: types.GET_LOCATIONS_ASYNC.PENDING, payload: true });

  try {
    const { postalCode } = payload;

    const locationsAPI = new API(`locations?postalCode=${postalCode}`);
    const response: Location[] = yield call(locationsAPI.getAll);

    yield put({ type: types.GET_LOCATIONS_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    let message = getErrorMessage(err as AxiosError);
    if (!message) message = i18n.t('locationDataCollection.errorOverlayHeader');

    yield put({ type: types.GET_LOCATIONS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.GET_LOCATIONS_ASYNC.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_CUSTOMER_ASYNC.REQUEST, getCustomerAsync),
  takeLatest(types.SAVE_CUSTOMER_ASYNC.REQUEST, saveCustomerAsync),
  takeLatest(types.SAVE_SURVEY_ASYNC.REQUEST, saveSurveyAsync),
  takeLatest(types.GET_LOCATIONS_ASYNC.REQUEST, getLocationsAsync)
];
